import styled from '@emotion/styled'
import { Card, Typography } from '@mui/material'
import { Box } from '@mui/system'
import BigNumber from 'bignumber.js'
import HorizontalText from 'components/container/HorizontalText'
import DropdownToken from 'components/DropdownToken'
import { AssetData } from 'constants/assets'
import { PERCENT } from 'constants/number'
import { Route } from 'interfaces/contracts/wrappedIrm'
import { IIRM } from 'interfaces/irm.interface'
import { observer } from 'mobx-react-lite'
import { useEffect, useMemo, useState } from 'react'
import { assetService } from 'services/assetService'
import { poolService } from 'services/poolService'
import { oracleStore, poolStore } from 'stores'
import { toA } from 'utils/display'
import { convertMemoToKind } from 'utils/simulateIRM'
import IRMChart from './IRMChart'

const CustomBox = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 0px 100px;
`

const CustomColumn = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-basis: 100%;
  flex: 1;
  min-width: min-content;
  padding: 4px 0px;
`

interface InfoProps {
  underlying: string
  supplyAPY: BigNumber
  borrowAPY: BigNumber
  price: BigNumber
  totalSupplied: BigNumber
  utilization: BigNumber
  collateralFactor: BigNumber
  borrowingFactor: BigNumber
  liquidationIncentive: BigNumber
  reserve: BigNumber
  reserveFactor: BigNumber
  irmRoute: Route
  irmConfig: IIRM
}

const AssetInfoCard = observer(() => {
  const poolAddr = poolService.poolAddress
  const { poolConfig, supplyMarketList, marketInfo } =
    poolStore.getPool(poolAddr)
  const prices = oracleStore.getPrices(poolService.poolAddress)
  const [selectedAsset, setSelectedAsset] = useState<AssetData>()
  const [info, setInfo] = useState<InfoProps>()

  const tokens = useMemo(() => {
    return (
      supplyMarketList?.map(
        (market) => assetService.assets[market.underlying],
      ) || []
    )
  }, [supplyMarketList])
  const adminFee = poolConfig ? parseFloat(poolConfig.fee_rate) : 0

  const onSelect = (token: AssetData) => {
    setSelectedAsset(token)
  }

  useEffect(() => {
    if (!supplyMarketList || selectedAsset) return
    setSelectedAsset(assetService.assets[supplyMarketList[0].underlying])
  }, [selectedAsset, supplyMarketList])

  useEffect(() => {
    if (!selectedAsset || !supplyMarketList) return
    const { terraAddress: underlyingAddr } = selectedAsset
    const m =
      supplyMarketList.find((market) => market.underlying === underlyingAddr) ??
      supplyMarketList[0]
    const _marketInfo = marketInfo[underlyingAddr]

    setInfo({
      ..._marketInfo,
      utilization: _marketInfo.utilization.times(PERCENT),
      price: prices[underlyingAddr],
      underlying: underlyingAddr,
      reserve: new BigNumber(m.total_reserve),
      reserveFactor: new BigNumber(poolConfig?.reserve_fee_rate || 0),
      totalSupplied: new BigNumber(m.total_credit)
        .plus(new BigNumber(m.total_loan))
        .div(`1e${selectedAsset.decimals}`),
      collateralFactor: new BigNumber(m.config.collateral_factor).times(
        PERCENT,
      ),
      borrowingFactor: new BigNumber(m.config.borrow_factor).times(PERCENT),
      liquidationIncentive: new BigNumber(m.config.liquidation_incentive).times(
        PERCENT,
      ),
    })
  }, [
    marketInfo,
    poolConfig?.reserve_fee_rate,
    prices,
    selectedAsset,
    supplyMarketList,
  ])

  return (
    <Card sx={{ border: 'none', overflow: 'hidden', padding: '0px' }}>
      <Box p="24px">
        <Box display="flex" justifyContent="space-between">
          <Typography variant="h6" mb="24px">
            Asset info
          </Typography>
          <Box width="140px">
            <DropdownToken
              selectedToken={selectedAsset}
              tokens={tokens}
              onSelect={onSelect}
            />
          </Box>
        </Box>

        <CustomBox>
          <CustomColumn>
            <HorizontalText
              left="Price"
              right={toA(info?.price)}
              prefix="$"
              rb
            />
            <HorizontalText
              left="Total supplied"
              right={toA(info?.totalSupplied)}
              suffix={selectedAsset?.symbol}
              rb
            />
            <HorizontalText
              left="Current utilization"
              infoTip="Utilization rate"
              right={toA(info?.utilization)}
              suffix="%"
              rb
            />
            <HorizontalText
              left="Reserve"
              right={toA(info?.reserve)}
              suffix={selectedAsset?.symbol}
              rb
            />
          </CustomColumn>
          <CustomColumn>
            <HorizontalText
              left="Reserve factor"
              infoTip="Reserve factor"
              right={toA(info?.reserveFactor)}
              suffix="%"
              rb
            />
            <HorizontalText
              left="Collateral factor"
              infoTip="Collateral factor"
              right={toA(info?.collateralFactor)}
              suffix="%"
              rb
            />
            <HorizontalText
              left="Borrowing factor"
              infoTip="Borrowing factor"
              right={toA(info?.borrowingFactor)}
              suffix="%"
              rb
            />
            <HorizontalText
              left="Liquidation incentive"
              infoTip="Liquidation incentive"
              right={toA(info?.liquidationIncentive)}
              suffix="%"
              rb
            />
          </CustomColumn>
        </CustomBox>
      </Box>
      <Box
        sx={{
          height: '220px',
          width: '100%',
          marginTop: '5px',
          marginBottom: '0px',
        }}
      >
        {info && (
          <IRMChart
            adminFee={adminFee}
            currentUtilization={parseFloat(toA(info?.utilization) || '0')}
            IRM={convertMemoToKind[info.irmRoute.memo]}
            irmInfo={info.irmConfig}
          />
        )}
      </Box>
    </Card>
  )
})

export default AssetInfoCard
